import { Button } from "@progress/kendo-react-buttons";
import { EditDescriptor } from "@progress/kendo-react-data-tools";
import { Grid, GridCellProps, GridColumn, GridItemChangeEvent } from "@progress/kendo-react-grid";
import { Error } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useEffect, useState } from "react";
import { emailValidator } from "../../validators";
import { ListItem } from "../form";
import { BooleanGridCell } from "../grid";
import { Commands, GridCommandCell } from "../grid/GridCommandCell";

interface IEmailListEditorProps {
  emails: any[];
  emailTypes: ListItem[];
  onChange: (emails: any[]) => void;
  onEdit?: (isEditing: boolean) => void;
}

export const EmailListEditor = ({
  emails,
  emailTypes,
  onChange,
  onEdit,
}: IEmailListEditorProps) => {
  const [editableEmails, setEditableEmails] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditing, setIsEditing] = useState<boolean>();
  const [edit, setEdit] = useState<EditDescriptor>({});

  const [newId, setNewId] = useState(-1);

  const mapEmails = (items: any[]) => {
    return items.map((item) => {
      const emailTypeIds: number[] = [];
      emailTypes.forEach((type) => {
        if (item[type.label]) emailTypeIds.push(type.value as number);
      });

      return { emailId: item.emailId, email: item.email, emailTypeIds };
    });
  };

  useEffect(() => {
    setEditableEmails(
      emails.map((item) => {
        const mappedItem = { ...item, key: item.emailId };
        emailTypes.forEach((emailType) => {
          mappedItem[emailType.label] = item.emailTypeIds.includes(emailType.value);
        });

        return mappedItem;
      }),
    );
  }, [emails]);

  useEffect(() => {
    setIsEditing(editableEmails.some((email) => email.inEdit));
    const editId = editableEmails.find((email) => email.inEdit)?.emailId;
    if (editId !== undefined) {
      setEdit({ [editId]: ["email"] });
    } else {
      setEdit({});
    }
  }, [editableEmails]);

  useEffect(() => {
    if (isEditing !== undefined) {
      onEdit && onEdit(isEditing);
      if (!isEditing) onChange(mapEmails(editableEmails));
    }
  }, [isEditing]);

  const emailIsValid = (item: any): boolean => {
    let error = emailValidator(item.email);

    if (
      editableEmails.some(
        (e) => item.email.toLowerCase() === e.email.toLowerCase() && item.emailId !== e.emailId,
      )
    )
      error = `${item.email} is already in the list`;

    setErrorMessage(error);
    return error.length === 0;
  };

  const handleCancel = (dataItem: any) => {
    const item = emails.find((email) => email.emailId === dataItem.emailId);
    const newGridData = editableEmails.map((email) => {
      return email.emailId === dataItem.emailId ? { ...item } : { ...email, inEdit: false };
    });

    setEditableEmails(newGridData);
    setErrorMessage("");
  };

  const handleDiscard = (_: any) => {
    setEditableEmails(editableEmails.filter((email) => !!email.emailId));
    setErrorMessage("");
  };

  const handleEdit = (dataItem: any) => {
    const newGridData = editableEmails.map((row) => {
      return row.emailId === dataItem.emailId ? { ...row, inEdit: true } : row;
    });

    setEditableEmails(newGridData);
  };

  const handleItemChange = ({ dataItem, field, value }: GridItemChangeEvent) => {
    const newGridData = editableEmails.map((item) => {
      return item.emailId === dataItem.emailId ? { ...item, [field as string]: value } : item;
    });

    setEditableEmails(newGridData);
  };

  const [newItemEmailId, setNewItemEmailId] = useState(-1);
  const handleCreate = (e: any) => {
    e.preventDefault();
    const item: any = { emailId: newItemEmailId, email: "", inEdit: true };
    emailTypes.forEach((type) => (item[type.label] = true));
    setEditableEmails([item].concat(editableEmails));
    setNewItemEmailId(newItemEmailId - 1);
  };

  const handleAdd = (dataItem: any) => {
    if (!emailIsValid(dataItem)) return;

    dataItem.emailId = newId;
    handleUpdate(dataItem);
    setNewId(newId - 1);
  };

  const handleUpdate = (dataItem: any) => {
    if (!emailIsValid(dataItem)) return;

    const newGridData = editableEmails.map((email) => {
      return email.emailId !== dataItem.emailId ? { ...email } : { ...dataItem, inEdit: false };
    });

    setEditableEmails(newGridData);
  };

  const handleRemove = (id: number) => {
    const newGridData = editableEmails.filter((item) => item.emailId !== id);
    setEditableEmails(newGridData);
    onChange(mapEmails(newGridData));
  };

  const commands: Commands = {
    onAdd: handleAdd,
    onUpdate: handleUpdate,
    onDiscard: handleDiscard,
    onCancel: handleCancel,
    onEdit: handleEdit,
    onRemove: handleRemove,
  };

  const GridCommandCellRender = (props: GridCellProps) => {
    const { dataItem } = props;
    const description = dataItem?.email ? `email ${dataItem.email}` : undefined;

    return (
      <GridCommandCell
        {...props}
        {...commands}
        idField="emailId"
        description={description}
        disabled={isEditing}
      />
    );
  };

  return (
    <Tooltip anchorElement="target" parentTitle={true}>
      <Button
        icon="plus"
        size="small"
        themeColor="primary"
        fillMode="outline"
        title="Add New Email"
        disabled={isEditing}
        onClick={handleCreate}
      />
      {!!errorMessage && <Error>{errorMessage}</Error>}

      {editableEmails.length > 0 && (
        <Grid
          data={editableEmails}
          editable={{ enabled: true, mode: "incell" }}
          edit={edit}
          onItemChange={handleItemChange}
          style={{ maxWidth: 600 }}
          dataItemKey="emailId"
        >
          <GridColumn field="email" title="Email" editor="text" />

          {emailTypes.map((item, index) => (
            <GridColumn
              field={item.label}
              title={item.label}
              key={`email_${item.label}_${index}`}
              width={115}
              className="align-center"
              editor="boolean"
              cells={{ data: BooleanGridCell }}
            />
          ))}

          <GridColumn cells={{ data: GridCommandCellRender }} width={80} editable={false} />
        </Grid>
      )}
    </Tooltip>
  );
};
